import { BlogCategory, BlogPost, CloudinaryBlogImage } from '../types/Blog';

/** Matches the server placeholder set. Indexing flags stay on those slugs. */
export const PLACEHOLDER_BLOG_SLUGS = new Set(['test-blog', 'blog-2']);

export function isPlaceholderBlogPost(post: Pick<BlogPost, 'slug'>): boolean {
  return PLACEHOLDER_BLOG_SLUGS.has(String(post.slug || '').toLowerCase());
}

export function imageSource(image?: CloudinaryBlogImage | null): string {
  if (!image) return '';
  return image.secure_url || image.url || '';
}

export function imageKey(image?: CloudinaryBlogImage | null): string {
  if (!image) return '';
  return image.publicId || image.public_id || imageSource(image);
}

function publishedTime(post: BlogPost): number {
  const value = Date.parse(post.publishedAt || post.createdAt || '');
  return Number.isNaN(value) ? 0 : value;
}

export function isPublicStory(post: BlogPost): boolean {
  if (!post.slug || post.noindex || isPlaceholderBlogPost(post)) return false;
  const status = post.status || (post.published === false ? 'draft' : 'published');
  if (status === 'draft') return false;
  const when = Date.parse((status === 'scheduled' ? post.scheduledFor : post.publishedAt) || '');
  if (status === 'scheduled') return !Number.isNaN(when) && when <= Date.now();
  return status === 'published' && (Number.isNaN(when) || when <= Date.now());
}

export function publishedPosts(posts: BlogPost[]): BlogPost[] {
  return posts
    .filter(isPublicStory)
    .slice()
    .sort((left, right) => publishedTime(right) - publishedTime(left));
}

export function genuinePosts(posts: BlogPost[]): BlogPost[] {
  return publishedPosts(posts).filter((post) => !isPlaceholderBlogPost(post));
}

export const HOMEPAGE_STORY_LIMIT = 6;

export function clampHomepageStoryCount(value: unknown): number {
  const count = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(count)) return 3;
  return Math.min(HOMEPAGE_STORY_LIMIT, Math.max(1, Math.round(count)));
}

/** Saved drag order. Until a story has been moved, the featured story still leads, then newest first. */
export function studioOrder(posts: BlogPost[]): BlogPost[] {
  if (posts.some((post) => Number(post.order) > 0)) {
    return posts.slice().sort((left, right) => (
      (Number(left.order) || Number.MAX_SAFE_INTEGER) - (Number(right.order) || Number.MAX_SAFE_INTEGER)
    ));
  }
  const byDate = posts.slice().sort((left, right) => publishedTime(right) - publishedTime(left));
  const featuredIndex = byDate.findIndex((post) => post.featured && isPublicStory(post));
  if (featuredIndex <= 0) return byDate;
  const featured = byDate[featuredIndex];
  return [featured, ...byDate.filter((post) => post.id !== featured.id)];
}

export function homepageStories(posts: BlogPost[], limit = 3): { lead: BlogPost | null; supporting: BlogPost[] } {
  const count = clampHomepageStoryCount(limit);
  const stories = studioOrder(posts).filter(isPublicStory);
  const lead = stories[0] || null;
  return { lead, supporting: stories.slice(1, count) };
}

export function formatStoryDate(value?: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function categoryLabel(categories: BlogCategory[], id: string): string {
  const match = categories.find((category) => category.id === id || category.slug === id);
  return match?.name || '';
}

export function postCategorySlugs(post: Pick<BlogPost, 'category' | 'categories'>): string[] {
  const slugs: string[] = [];
  const push = (value?: string) => {
    const slug = String(value || '').trim().toLowerCase();
    if (!slug || slugs.includes(slug)) return;
    slugs.push(slug);
  };
  push(post.category);
  (post.categories || []).forEach((id) => push(id));
  return slugs;
}

export function categoryNames(post: BlogPost, categories: BlogCategory[]): string[] {
  return postCategorySlugs(post)
    .map((slug) => categoryLabel(categories, slug))
    .filter(Boolean);
}

export function primaryCategory(post: BlogPost, categories: BlogCategory[]): string {
  return categoryNames(post, categories)[0] || '';
}

export function categoriesInUse(posts: BlogPost[], categories: BlogCategory[]): BlogCategory[] {
  const used = new Set<string>();
  publishedPosts(posts).forEach((post) => {
    postCategorySlugs(post).forEach((id) => used.add(id));
  });
  return categories
    .filter((category) => (used.has(category.id) || used.has(category.slug)) && String(category.seoTitle || '').trim())
    .slice()
    .sort((left, right) => (left.order || 0) - (right.order || 0) || left.name.localeCompare(right.name));
}

export function articleImages(post: BlogPost): CloudinaryBlogImage[] {
  const seen = new Set<string>();
  const images: CloudinaryBlogImage[] = [];
  const push = (image?: CloudinaryBlogImage) => {
    const key = imageKey(image);
    if (!image || !imageSource(image) || !key || seen.has(key)) return;
    seen.add(key);
    images.push(image);
  };

  push(post.heroImage);
  (post.galleryImages || []).forEach(push);
  return images;
}

export function plainStoryText(value?: string): string {
  return String(value || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function nextStory(posts: BlogPost[], currentSlug: string): BlogPost | null {
  const stories = genuinePosts(posts);
  if (stories.length < 2) return null;
  const index = stories.findIndex((post) => post.slug === currentSlug);
  if (index === -1) return stories[0];
  return stories[(index + 1) % stories.length];
}
